import { FlaskConical } from "lucide-react";
import { Sub, RefLink, P, Pill, ConnectsTo } from "./GuideHelpers";
import { PageCard } from "./PageCard";

export const SectionProductTabsAbTests = () => (
  <div id="ab-tests" className="scroll-mt-6">
    <PageCard icon={FlaskConical} title="A/B Tests" route="Tab 6" purpose="Content experiments to measure the impact of listing changes on rank and conversions — the engine that turns recommendations into evidence.">
      <P>
        A/B Tests are the heart of Signal's evidence loop. Every other surface
        (SEO Insights, Keywords, Landscapes, Content Scorecard, Brand Signals) ultimately
        funnels into a test, and every completed test funnels back out into the
        brand's <RefLink to="brand-signals">Brand Signals</RefLink> pool. Today tests compare
        two versions of the <strong>title</strong> and/or <strong>bullet points</strong> —
        the two listing elements Signal currently supports — and measure which performs better
        against a defined hypothesis. (Image and A+ content tests are out of scope for now.)
      </P>

      <Sub>Where tests can be triggered from</Sub>
      <P>
        Every entry point routes to the same unified creation flow
        (<code className="text-[11px] bg-surface-tint-medium px-1.5 py-0.5 rounded">/ab-tests/create</code>),
        carrying context so the form is pre-filled with the right hypothesis, hero ASIN,
        and source attribution. All current triggers are wired:
      </P>
      <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
        <li>
          <strong className="text-foreground">Content Scorecard (product tab)</strong> —
          Each COSMO gap card has an <Pill>A/B test</Pill> button. Clicking it pre-fills
          the test with the failing question, the AI's recommendation, and the evidence
          snippet. <em>Source tag:</em> <code>scorecard</code>.
        </li>
        <li>
          <strong className="text-foreground">Brand Signals drawer</strong> — Opening any
          signal exposes a <Pill>Create test</Pill> CTA (both per-ASIN and global). The
          flow pre-fills with the signal's hypothesis matrix so the new test is wired to
          contribute votes back to that same signal. <em>Source tag:</em> <code>brand-signal</code>.
        </li>
        <li>
          <strong className="text-foreground">SEO Insights (product tab)</strong> — The
          per-row <Pill>Generate A/B Test</Pill> icon and the bulk-action{" "}
          <Pill>Run A/B Test</Pill> on both the Signal AI Recommendations rail and the
          standard keyword table launch the flow with the selected keyword seeded as the
          target phrase. <em>Source tag:</em> <code>seo-insight</code>.
        </li>
        <li>
          <strong className="text-foreground">Keywords (product tab)</strong> — Both the
          per-row kebab <Pill>Run A/B Test</Pill> and the bulk-action equivalent launch
          the flow with the selected keyword seeded as the target phrase.{" "}
          <em>Source tag:</em> <code>keyword</code>.
        </li>
        <li>
          <strong className="text-foreground">Landscapes (unified landscape table)</strong> —
          The per-row kebab <Pill>Generate A/B Test</Pill> launches the flow with the
          landscape's hero ASIN and the row's keyword seeded as the target phrase.{" "}
          <em>Source tag:</em> <code>landscape</code>.
        </li>
        <li>
          <strong className="text-foreground">A/B Tests table — Resume Draft</strong> —
          Any test saved with status <Pill>Drafting</Pill> can be reopened from the row
          (or from the kebab "Edit"), restoring the in-progress modal state.
        </li>
        <li>
          <strong className="text-foreground">Upload past test</strong> — Logs a test that
          was already run outside Signal. Accepts a results CSV or screenshot and creates
          a completed test row directly, skipping the planning phase.{" "}
          <em>Source tag:</em> <code>upload</code>.
        </li>
      </ul>

      <Sub>Lifecycle states</Sub>
      <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
        <li><Pill>Drafting</Pill> — Test was opened but not finalized. Clicking the row reopens the create flow.</li>
        <li><Pill>Planning</Pill> — Test is fully configured in Signal and awaiting setup in Amazon Manage Your Experiments.</li>
        <li><Pill>Running</Pill> — Live in Amazon, being tracked in ClickUp. Metrics are not yet in Signal.</li>
        <li><Pill>Complete</Pill> — Results uploaded; a winning variant has been recorded and the test now contributes to Brand Signals.</li>
        <li><Pill variant="muted">Archived</Pill> — Excluded from active dashboards and rollups but preserved for history.</li>
      </ul>

      <Sub>Results upload</Sub>
      <P>
        Two paths, depending on whether the test was launched from Signal:
      </P>
      <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
        <li>
          <strong className="text-foreground">Upload Results</strong> — For a Running test.
          Captures Amazon metrics (CVR, sales, sessions), an optional Variant B switch
          date, and the outcome. On submit the test flips to <Pill>Complete</Pill> and the
          winning variant is recorded.
        </li>
        <li>
          <strong className="text-foreground">Upload Past Test</strong> — For a historical
          test that lives outside Signal. Drag-and-drop a CSV or screenshot; Signal parses
          it, asks for any missing metadata, and lands the result as a completed row.
        </li>
      </ul>

      <Sub>How a completed test feeds Brand Signals</Sub>
      <P>
        Each test carries a <strong>hypothesis matrix</strong> — the set of intent, keyword,
        and content-axis claims it is making. When the test completes, Signal reads the
        <strong> winning variant's matrix only</strong> and contributes a <strong>supporting
        vote</strong> to every matching entry in the brand's signal pool. The losing
        variant's matrix is intentionally <strong>discarded</strong> — we do not issue
        refuting votes. Rationale: in a two-cell experiment the losing variant says very
        little about <em>why</em> it lost (confound between intent, keyword choice, copy
        axis, length, lead phrase, etc.), so treating its claims as evidence against
        matching signals would inject false-negative noise into the brand pool. Aggregating
        only supporting votes across many small experiments is what lets a handful of
        one-off tests accrete into a durable, brand-level point of view.
      </P>

      <Sub>The Create Test flow — orientation</Sub>
      <P>
        Every trigger lands in <code className="text-[11px] bg-surface-tint-medium px-1.5 py-0.5 rounded">CreateTestModal</code>
        (rendered full-page at <code className="text-[11px] bg-surface-tint-medium px-1.5 py-0.5 rounded">/ab-tests/create</code>).
        The flow has two steps, separated by a <Pill>Continue</Pill> click:
      </P>
      <ol className="list-decimal pl-5 space-y-1 text-sm text-muted-foreground">
        <li>
          <strong className="text-foreground">Choose which fields to test</strong> — a
          single-column step listing the testable fields: <strong>Title</strong> and
          <strong> Bullets 1–5</strong>. Select one or more via checkbox. Selecting more
          than one surfaces an inline amber warning that multi-field tests reduce result
          specificity. There is no intent picker, target-keyword input, or AI-drafted
          hypothesis sentence at this step — the hypothesis is captured implicitly by
          the Variant B copy produced in step 2 (and explicitly by the 5-axis fingerprint
          matrix the system records on save). <Pill>Continue</Pill> advances to step 2.
        </li>
        <li>
          <strong className="text-foreground">Build Variant B</strong> — a two-pane editor:
          <strong> Variant A</strong> on the left (read-only, pinned to the current live
          listing, color-coded by axis with a legend and live char count), and
          <strong> Variant B</strong> on the right with editable field cards plus a
          build-method picker below. The Variant B header carries a <Pill>Copy</Pill>
          button (clipboard-ready content for Amazon Manage Your Experiments) and a
          <Pill>Reset</Pill> button that snaps every field back to Variant A and clears
          AI tagging. The header strip shows which fields are under test with an
          <Pill>Edit</Pill> link to revise the selection without losing draft state.
        </li>
      </ol>
      <P>
        There is no separate "Review &amp; save" step — review is continuous, because
        Variant A and Variant B are always side-by-side. The footer exposes two actions
        at all times, and the choice the user makes determines the lifecycle status the
        test lands in:
      </P>
      <ul className="list-disc pl-5 space-y-1.5 text-sm leading-relaxed text-muted-foreground">
        <li>
          <Pill>Save Test</Pill> — Parks the in-progress test in the A/B Tests table as
          <Pill>Planning</Pill> with relaxed validation (Variant B can still be empty or
          partial). Use this when the user is <strong>not</strong> ready to launch yet
          and wants to come back to keep iterating, share with a teammate, or wait on
          upstream input.
        </li>
        <li>
          <Pill>Create Test</Pill> — Assumes the user is <strong>launching the test right now</strong>.
          Requires every selected field to have non-empty Variant B copy, and lands the
          test directly in <Pill>Running</Pill>. A success dialog surfaces the Variant B
          payload (Copy button) so the user can paste it into Amazon Manage Your
          Experiments to make the live experience match what Signal is now tracking.
        </li>
      </ul>
      <P>
        In short: <strong>Save Test = Planning</strong> (parked draft), <strong>Create
        Test = Running</strong> (live now). A test saved as Planning can later be opened
        and promoted to Running by hitting Create Test from the editor.
      </P>

      <Sub>Build Variant B — the three modes</Sub>
      <P>
        Below the Variant B draft, a segmented control switches between three build modes.
        They are <strong>not mutually exclusive over the life of the draft</strong> — a
        user can start with AI, then nudge in the Manual Editor, then re-run a treatment.
        Every AI-touched field is tagged so the user can see which words came from Signal,
        and a per-field "edited" flag is raised the moment the user types over AI output
        so subsequent AI runs never silently overwrite a user-typed phrase.
      </P>

      <p className="text-sm text-muted-foreground leading-relaxed mb-2"><strong className="text-foreground">1. Use AI</strong> — the default tab.</p>
      <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
        <li>
          <strong className="text-foreground">Let AI do its magic → Generate best
          recommendation</strong> — one click. Signal AI synthesizes brand signals, the
          content scorecard, tracked keywords, and industry priors, then writes a
          Variant B for every selected field. A <strong>Signal AI rationale drawer</strong>
          becomes available on the right showing the levers used, phrase-level rewrites
          (A → B with reasoning), preserved fragments, and dropped fragments.
        </li>
        <li>
          <strong className="text-foreground">Format treatments</strong> — small, targeted
          cards beneath the magic button that don't change the message, only the shape:
          <strong> Lead phrase</strong> (benefit / audience / ingredient / use-case-led —
          implemented as a pure reorder of the title's axis zones, not a rewrite),
          <strong> Length</strong> (shorter / longer, clamped to the 200-char title budget
          or shortest-possible product-name-only floor), and
          <strong> Hyphenation &amp; punctuation</strong> (minimal / moderate / heavy).
          Disabled states are self-explanatory (e.g. "Already leading the Variant B
          title", "Title is already at the 200-character limit").
        </li>
      </ul>

      <p className="text-sm text-muted-foreground leading-relaxed mt-3 mb-2"><strong className="text-foreground">2. From a starting point</strong> — guided AI seeded by one piece of evidence.</p>
      <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
        <li>First, a single column of three source cards: <Pill>Brand signal</Pill>, <Pill>Keyword</Pill>, <Pill>Scorecard gap</Pill>. Each card shows a count badge so the user knows how much evidence is available before drilling in.</li>
        <li><strong className="text-foreground">Brand signal</strong> — picker lists all active signals for this product's brand. Click one and Signal AI rewrites the selected fields to test that signal's strategy. The resulting completed test contributes votes back to that exact signal on completion.</li>
        <li><strong className="text-foreground">Keyword</strong> — picker lists the highest-KPS tracked keywords currently unused on this listing. Click one and AI weaves it into the chosen fields with reasoning ("ranking #X with Y monthly searches").</li>
        <li><strong className="text-foreground">Scorecard gap</strong> — picker lists the lowest-scoring COSMO questions on this product's content scorecard, with a "View full content scorecard" affordance that opens the scorecard in a side drawer. Click a gap and AI rewrites the chosen fields to address it, citing the evidence snippet.</li>
      </ul>

      <p className="text-sm text-muted-foreground leading-relaxed mt-3 mb-2"><strong className="text-foreground">3. Manual Editor</strong> — zone-level control, no AI required.</p>
      <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
        <li>
          On open (and on every Variant B change), Signal AI runs a <strong>5-axis
          fingerprint pass</strong> on the listing, parsing each selected field into
          <strong> zones</strong> — small spans of text tagged with a positioning axis
          (<em>benefit, audience, use-case, ingredient, form</em>) plus the immutable
          <strong> Product Name</strong> zone, which can be reordered but never deleted
          or rewritten.
        </li>
        <li>
          <strong className="text-foreground">Drag</strong> a zone card to reorder it.
          <strong> Click</strong> a zone card to open the third pane (Zone Edit Drawer):
          edit the text inline, swap its axis, or apply an AI-generated alternative that
          preserves the zone's axis and meaning.
        </li>
        <li>
          The Variant B preview, axis legend, and char count update live as zones change.
          Manual edits are tracked separately from AI edits, so re-running an AI treatment
          never silently overwrites typed text.
        </li>
      </ul>

      <ConnectsTo pages={["Content Scorecard", "Keywords", "Landscapes", "SEO Insights", "Brand Signals", "Rank Tracker"]} />
    </PageCard>
  </div>
);
